import type { NetworkSortType } from "../components/Network/NetworkSortMenu/NetworkSortMenu";

interface NetworkFilterable {
  user: {
    first_name: string;
    last_name: string;
  };
  company: {
    name?: string;
  } | null;
  branch?: {
    name?: string;
  } | null;
  created_at: string;
}

/**
 * Filters by search query and sorts connection items for network tables
 * Searching:
 * - Searches across user's first_name + last_name (full contact name)
 * - Searches across company name (if present)
 * - Searches across branch name (if present)
 * - Case-insensitive substring matching
 * Sorting options:
 * - CONTACT_NAME: Alphabetical by user's full name (first + last)
 * - COMPANY_NAME: Alphabetical by company name
 * - DATE_ADDED: By created_at timestamp (newest first)
 * @param items Array of network items to filter and sort
 * @param sortType How to sort the results
 * @param searchQuery Text to filter by (searches contact name, company name, and branch name)
 * @returns Filtered and sorted array preserving original item type
 */
export function filterAndSortNetworkItems<T extends NetworkFilterable>(
  items: T[],
  sortType: NetworkSortType,
  searchQuery: string,
): T[] {
  // First filter by search query
  let filtered = items;
  if (searchQuery.trim()) {
    const query = searchQuery.toLowerCase().trim();
    filtered = items.filter((item) => {
      const fullName =
        `${item.user.first_name} ${item.user.last_name}`.toLowerCase();
      const companyName = item.company?.name?.toLowerCase() ?? "";
      const branchName = item.branch?.name?.toLowerCase() ?? "";
      return (
        fullName.includes(query) ||
        companyName.includes(query) ||
        branchName.includes(query)
      );
    });
  }

  // Then sort the filtered results
  return [...filtered].sort((a, b) => {
    if (sortType === "CONTACT_NAME") {
      const aName = `${a.user.first_name} ${a.user.last_name}`.toLowerCase();
      const bName = `${b.user.first_name} ${b.user.last_name}`.toLowerCase();
      return aName.localeCompare(bName);
    }

    if (sortType === "COMPANY_NAME") {
      const aCompany = a.company?.name?.toLowerCase() ?? "";
      const bCompany = b.company?.name?.toLowerCase() ?? "";
      return aCompany.localeCompare(bCompany);
    }

    if (sortType === "DATE_ADDED") {
      // Sort by date added (newest first - descending order)
      return (
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
    }

    return 0;
  });
}
